import { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  formatQuantity,
  type CanonicalUnit,
  type CreateEntryInput,
  type DishEstimate,
  type MealType,
  type ParsedItem,
  type RecentItem,
  type Recipe,
} from '@nutrigeist/shared';
import { diaryApi, intakeApi, recipeApi } from '../api/endpoints.js';
import { ApiError } from '../api/client.js';
import {
  BarcodePanel,
  LabelPanel,
  PhotoPanel,
  SearchPanel,
  type PickedFood,
} from '../components/foodSources.js';
import { LogFoodSheet, type LoggableFood } from '../components/LogFoodSheet.js';
import { DishEstimateCard } from '../components/DishEstimateCard.js';
import {
  BarcodeIcon,
  CameraIcon,
  ClockIcon,
  PlateIcon,
  RecipeIcon,
  SearchIcon,
  TextIcon,
} from '../components/icons.js';
import { MEAL_LABELS, formatGrams, formatKcal } from '../lib/format.js';

type Mode = 'recent' | 'search' | 'barcode' | 'label' | 'photo' | 'text' | 'recipe';

const MODES: { id: Mode; label: string; Icon: (props: { className?: string }) => JSX.Element }[] = [
  { id: 'recent', label: 'Kürzlich', Icon: ClockIcon },
  { id: 'search', label: 'Suche', Icon: SearchIcon },
  { id: 'barcode', label: 'Barcode', Icon: BarcodeIcon },
  { id: 'label', label: 'Label', Icon: CameraIcon },
  { id: 'photo', label: 'Foto', Icon: PlateIcon },
  { id: 'text', label: 'Text', Icon: TextIcon },
  { id: 'recipe', label: 'Rezept', Icon: RecipeIcon },
];

interface PendingLog {
  food: LoggableFood;
  quantity?: { amount: number; unit: CanonicalUnit } | null;
  warnings?: string[];
  mealType?: MealType;
}

export function AddPage(): JSX.Element {
  // Re-logging something eaten recently is the most common action, so it is
  // what the screen opens on.
  const [mode, setMode] = useState<Mode>('recent');
  const [pending, setPending] = useState<PendingLog | null>(null);
  // Carried from an unknown barcode into the label scan, so the captured
  // product can be filed under it.
  const [labelBarcode, setLabelBarcode] = useState<string | null>(null);

  const closeSheet = useCallback(() => setPending(null), []);
  const handlePicked = useCallback((picked: PickedFood) => {
    // The quantity is carried through when a panel knows one. An estimated
    // dish describes exactly one portion, so dropping it here would reopen the
    // sheet at 100 g and quietly rescale the numbers the user just read.
    setPending({ food: picked.food, warnings: picked.warnings, quantity: picked.quantity });
  }, []);

  return (
    <div className="stack add-page">
      <h1>Hinzufügen</h1>

      {mode === 'recent' && <RecentPanel onPick={setPending} />}

      {mode === 'search' && <SearchPanel onPicked={handlePicked} />}

      {mode === 'barcode' && (
        <BarcodePanel
          onPicked={handlePicked}
          onScanLabel={(barcode) => {
            setLabelBarcode(barcode);
            setMode('label');
          }}
        />
      )}

      {mode === 'label' && (
        <LabelPanel
          onPicked={handlePicked}
          onCancel={() => {
            setLabelBarcode(null);
            setMode('search');
          }}
          barcode={labelBarcode}
        />
      )}

      {mode === 'photo' && (
        <PhotoPanel onPicked={handlePicked} onCancel={() => setMode('search')} />
      )}

      {mode === 'text' && <TextPanel onPick={setPending} />}

      {mode === 'recipe' && <RecipePanel onPick={setPending} />}

      {/* The mode switcher lives at the bottom, just above the nav bar: it is
          the control used most often on this screen, and the top of a phone
          is the hardest place to reach one-handed. */}
      <div className="mode-switcher">
        <div className="segmented">
          {MODES.map(({ id, label, Icon }) => (
            <button
              key={id}
              className={mode === id ? 'active' : ''}
              onClick={() => {
                // Only the barcode handoff should carry a code into the label
                // scan; a manual switch starts it clean.
                setLabelBarcode(null);
                setMode(id);
              }}
              aria-pressed={mode === id}
            >
              <Icon />
              <span>{label}</span>
            </button>
          ))}
        </div>
      </div>

      {pending && (
        <LogFoodSheet
          food={pending.food}
          initialQuantity={pending.quantity ?? null}
          initialMealType={pending.mealType}
          warnings={pending.warnings}
          onClose={closeSheet}
          onLogged={closeSheet}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Recently logged
// ---------------------------------------------------------------------------

/** Rebuilds something loggable from a previously logged entry. */
function recentAsFood(item: RecentItem): LoggableFood {
  return {
    id: item.foodId,
    name: item.name,
    brand: item.brand,
    basis: item.basis,
    nutrients: item.per100,
    // Recovered from the original entry, so a "2 Stück" item can be re-logged
    // as pieces even when no catalog food stands behind it.
    serving: item.servingGrams ? { grams: item.servingGrams, label: '1 Portion' } : null,
    density: null,
  };
}

function RecentPanel({ onPick }: { onPick: (pending: PendingLog) => void }): JSX.Element {
  const recent = useQuery({ queryKey: ['diary', 'recent'], queryFn: () => diaryApi.recent(7) });

  if (recent.isPending) {
    return (
      <div className="card empty-state">
        <div className="spinner" style={{ margin: '0 auto' }} role="status" aria-label="Lädt" />
      </div>
    );
  }

  const meals = (recent.data?.meals ?? []).filter((meal) => meal.items.length > 0);

  if (meals.length === 0) {
    return (
      <div className="card empty-state">
        Nichts in den letzten 7 Tagen erfasst. Was du einmal erfasst hast, erscheint hier zum
        schnellen Wiederholen.
      </div>
    );
  }

  return (
    <div className="stack">
      {meals.map((meal) => (
        <div className="card meal-group" key={meal.mealType}>
          <div className="meal-heading">
            <span>{MEAL_LABELS[meal.mealType] ?? meal.mealType}</span>
            <span>{meal.items.length}</span>
          </div>

          <div className="entry-list">
            {meal.items.map((item) => (
              <button
                key={`${meal.mealType}-${item.foodId ?? item.name}`}
                className="entry entry-button"
                onClick={() =>
                  onPick({
                    food: recentAsFood(item),
                    quantity: item.quantity,
                    mealType: meal.mealType,
                  })
                }
              >
                <div className="grow">
                  <div className="entry-name">{item.name}</div>
                  <div className="entry-meta">
                    {formatQuantity(item.quantity)}
                    {item.brand ? ` · ${item.brand}` : ''}
                    {item.timesLogged > 1 ? ` · ${item.timesLogged}×` : ''}
                  </div>
                </div>
                <span className="entry-kcal">
                  {formatKcal((item.per100.kcal * quantityGrams(item)) / 100)} kcal
                </span>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/** The amount, in the food's basis unit, that the saved quantity represents. */
function quantityGrams(item: RecentItem): number {
  if (item.quantity.unit === 'piece' || item.quantity.unit === 'serving') {
    return (item.servingGrams ?? 0) * item.quantity.amount;
  }
  return item.quantity.amount;
}

// ---------------------------------------------------------------------------
// Recipes
// ---------------------------------------------------------------------------

/**
 * Turns a saved recipe into something loggable.
 *
 * The recipe's derived per-100 g values are sent inline rather than as a
 * catalog reference, so the diary entry keeps the numbers as they were when it
 * was logged — editing the recipe afterwards must not restate past meals.
 */
function recipeAsFood(recipe: Recipe): LoggableFood {
  return {
    id: null,
    name: recipe.name,
    brand: null,
    basis: 'per_100g',
    nutrients: recipe.nutrients,
    serving: recipe.serving,
    density: null,
  };
}

function RecipePanel({ onPick }: { onPick: (pending: PendingLog) => void }): JSX.Element {
  const recipes = useQuery({ queryKey: ['recipes'], queryFn: () => recipeApi.list() });

  if (recipes.isPending) {
    return (
      <div className="card empty-state">
        <div className="spinner" style={{ margin: '0 auto' }} role="status" aria-label="Lädt" />
      </div>
    );
  }

  const usable = (recipes.data?.recipes ?? []).filter((recipe) => recipe.totalGrams > 0);

  if (usable.length === 0) {
    return (
      <div className="card empty-state">
        Noch keine Rezepte mit Zutaten. Lege eines im Tab <strong>Rezepte</strong> an.
      </div>
    );
  }

  return (
    <div className="card">
      <div className="entry-list">
        {usable.map((recipe) => (
          <button
            key={recipe.id}
            className="entry entry-button"
            onClick={() =>
              onPick({
                food: recipeAsFood(recipe),
                // Default to one portion where the recipe defines them,
                // otherwise to a round 100 g the user can adjust.
                quantity: recipe.serving
                  ? { amount: 1, unit: 'serving' }
                  : { amount: 100, unit: 'g' },
              })
            }
          >
            <div className="grow">
              <div className="entry-name">{recipe.name}</div>
              <div className="entry-meta">
                {formatGrams(recipe.totalGrams)} g gesamt · {formatKcal(recipe.nutrients.kcal)} kcal
                / 100 g{recipe.servings ? ` · ${recipe.servings} Portionen` : ''}
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Free text
// ---------------------------------------------------------------------------

function TextPanel({ onPick }: { onPick: (pending: PendingLog) => void }): JSX.Element {
  const queryClient = useQueryClient();
  const [text, setText] = useState('');
  const [items, setItems] = useState<ParsedItem[] | null>(null);
  const [dish, setDish] = useState<DishEstimate | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(0);

  const parse = useMutation({
    mutationFn: (input: string) => intakeApi.parseText({ text: input, allowEstimates: true }),
    onSuccess: (result) => {
      setItems(result.items);
      setDish(null);
      setError(null);
      setSaved(0);
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Text konnte nicht gelesen werden.'),
  });

  const estimateDish = useMutation({
    mutationFn: (input: { name: string; servings: number }) => intakeApi.estimateDish(input),
    onSuccess: (result) => {
      setDish(result);
      setItems(null);
      setError(null);
      setSaved(0);
    },
    onError: (caught) =>
      setError(
        caught instanceof ApiError ? caught.message : 'Das Gericht konnte nicht geschätzt werden.',
      ),
  });

  const resolved = (items ?? []).filter((item) => item.match != null);

  const save = useMutation({
    mutationFn: async () => {
      const entries: CreateEntryInput[] = resolved.map((item) => {
        const food = item.match!.food;
        // With no amount stated, 100 g/ml is the honest default and the user
        // can correct it on the Today screen.
        const quantity = item.quantity ?? {
          amount: 100,
          unit: (food.basis === 'per_100ml' ? 'ml' : 'g') as CanonicalUnit,
        };

        return {
          food:
            item.match!.matchedVia === 'llm_estimate'
              ? {
                  name: food.name,
                  brand: food.brand,
                  basis: food.basis,
                  nutrients: food.nutrients,
                  serving: food.serving,
                  density: food.density,
                  saveToCatalog: false,
                }
              : { foodId: food.id },
          quantity,
          mealType: 'snack' as const,
        };
      });

      const result = await diaryApi.createEntries(
        entries,
        entries.length === 1 ? 'ein Eintrag' : `${entries.length} Einträge`,
      );

      // A queued write has no server response to count, so the count comes
      // from what was submitted — it is the same number either way.
      return { count: result.data?.entries.length ?? entries.length };
    },
    onSuccess: async ({ count }) => {
      await queryClient.invalidateQueries({ queryKey: ['diary'] });
      setSaved(count);
      setItems(null);
      setText('');
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Speichern fehlgeschlagen.'),
  });

  return (
    <div className="stack">
      <div className="field">
        <label htmlFor="freetext">Beschreibe, was du gegessen hast</label>
        <textarea
          id="freetext"
          className="textarea"
          placeholder="300g hühnerbrust 100g frischkäse, 200g reis"
          value={text}
          onChange={(event) => setText(event.target.value)}
        />
      </div>

      <button
        className="btn btn-primary btn-block"
        disabled={parse.isPending || text.trim().length === 0}
        onClick={() => parse.mutate(text.trim())}
      >
        {parse.isPending ? <span className="spinner" /> : 'Auswerten'}
      </button>

      {/* For a dish named without amounts ("chili con carne") there is nothing
          for the parser to weigh, so offer the ingredient breakdown instead. */}
      <button
        className="btn btn-ghost btn-block"
        disabled={estimateDish.isPending || text.trim().length < 2}
        onClick={() => estimateDish.mutate({ name: text.trim(), servings: 1 })}
      >
        {estimateDish.isPending ? <span className="spinner" /> : 'Als Gericht schätzen'}
      </button>

      <span className="hint">
        „Auswerten“ liest Mengenangaben. „Als Gericht schätzen“ zerlegt ein Gericht wie „Chili con
        Carne“ in seine Zutaten.
      </span>

      {error && (
        <div className="banner banner-error" role="alert">
          {error}
        </div>
      )}

      {saved > 0 && (
        <div className="banner banner-info" role="status">
          {saved} {saved === 1 ? 'Eintrag' : 'Einträge'} gespeichert.
        </div>
      )}

      {dish && <DishEstimateCard dish={dish} onPick={onPick} />}

      {items && (
        <div className="card stack">
          <div className="card-title">Erkannt</div>

          <div className="entry-list">
            {items.map((item, index) => (
              <div className="entry" key={`${item.raw}-${index}`}>
                <div className="grow">
                  <div className="entry-name">{item.match?.food.name ?? item.query}</div>
                  <div className="entry-meta">
                    {item.quantity ? formatQuantity(item.quantity) : '100 g (Standard)'}
                    {item.match ? '' : ' · nicht erkannt'}
                  </div>
                </div>

                {item.match ? (
                  <span
                    className={
                      item.match.matchedVia === 'llm_estimate' ? 'badge badge-warning' : 'badge'
                    }
                  >
                    {item.match.matchedVia === 'llm_estimate' ? 'Schätzung' : 'Treffer'}
                  </span>
                ) : (
                  <span className="badge badge-warning">?</span>
                )}
              </div>
            ))}
          </div>

          {resolved.some((item) => item.match?.matchedVia === 'llm_estimate') && (
            <div className="banner banner-warning">
              Einige Werte sind Schätzungen eines Sprachmodells und keine gemessenen Nährwerte.
            </div>
          )}

          <button
            className="btn btn-primary btn-block"
            disabled={save.isPending || resolved.length === 0}
            onClick={() => save.mutate()}
          >
            {save.isPending ? (
              <span className="spinner" />
            ) : (
              `${resolved.length} ${resolved.length === 1 ? 'Eintrag' : 'Einträge'} speichern`
            )}
          </button>
        </div>
      )}
    </div>
  );
}
